import { Constants } from "./constants";
import { isUkcRallyShow, ukcRallyLevelSections } from "./ukc-rally-classes";

/**
 * Show Type Definitions
 * 
 * Centralized definition of show types, elements, levels, and sections
 * Used by both frontend (show creation/registration) and backend (judge schedule generation)
 * 
 * Structure:
 * {
 *   [sanctioning_club]: {
 *     [show_type]: {
 *       [element_name]: levels[] | { levels: [], sections: [] }
 *     }
 *   }
 * }
 * 
 * For simple shows: element maps directly to level array
 * For complex shows: element maps to config object with levels and optional sections
 */

export interface IShowTypeElementConfig {
  levels: string[];
  sections?: string[]; // Optional: for shows that use sections (UKC Nosework, C-WAGS Obedience/Rally, Herding)
}

// C-WAGS Games level sets (shared by every game element)
export const CWAGS_GAMES_STANDARD_LEVELS: string[] = [
  Constants.dog_class_element_level.cwags_games_level_1,
  Constants.dog_class_element_level.cwags_games_level_2,
  Constants.dog_class_element_level.cwags_games_level_3,
  Constants.dog_class_element_level.cwags_games_level_4,
];
export const CWAGS_GAMES_ZOOM_LEVELS: string[] = [
  Constants.dog_class_element_level.cwags_games_zoom_1,
  Constants.dog_class_element_level.cwags_games_zoom_1_5,
  Constants.dog_class_element_level.cwags_games_zoom_2,
];
const CWAGS_GAMES_RALLY_LEVELS: string[] = [
  ...CWAGS_GAMES_STANDARD_LEVELS,
  ...CWAGS_GAMES_ZOOM_LEVELS,
];
// Grab Bag Games (C-WAGS "Grab Bag Games" 2013 rules): Levels 1, 2, 3 only — no Level 4,
// no Zoom variant. Each level = 3 pass/fail challenges picked by the judge from a list.
export const CWAGS_GAMES_GRAB_BAG_LEVELS: string[] = [
  Constants.dog_class_element_level.cwags_games_level_1,
  Constants.dog_class_element_level.cwags_games_level_2,
  Constants.dog_class_element_level.cwags_games_level_3,
];

// FEO (For Exhibition Only) — offered by all four C-WAGS show types. Stored as
// dog_class.section = Constants.dog_class_section.feo. Obedience / Rally offer it per level
// in show setup (like A/B/TO/JR); Scent / Games offer it show-wide via show.feo_available.
export const CWAGS_FEO_SHOW_TYPES: string[] = [
  Constants.show_type.obedience,
  Constants.show_type.rally_obedience,
  Constants.show_type.scent,
  Constants.show_type.cwags_games,
];
export const CWAGS_FEO_SHOW_WIDE_SHOW_TYPES: string[] = [
  Constants.show_type.scent,
  Constants.show_type.cwags_games,
];
export const isCwagsFeoEligible = (sanctioningClub?: string, showType?: string): boolean =>
  sanctioningClub === Constants.sanctioning_club.C_WAGS &&
  !!showType &&
  CWAGS_FEO_SHOW_TYPES.includes(showType);
// True for the show types where FEO is a single show-wide toggle (Scent / Games) rather than
// a per-level section checkbox (Obedience / Rally).
export const isCwagsFeoShowWide = (sanctioningClub?: string, showType?: string): boolean =>
  sanctioningClub === Constants.sanctioning_club.C_WAGS &&
  !!showType &&
  CWAGS_FEO_SHOW_WIDE_SHOW_TYPES.includes(showType);

// Main definitions - supports both simple array and complex config
export const ShowTypes: any = {
  [Constants.sanctioning_club.UKC]: {
    [Constants.show_type.nosework]: {
      // Sections A and B are added per-entry during registration, not at show setup
      [Constants.dog_class_element.nosework_container]: [
        Constants.dog_class_element_level.novice,
        Constants.dog_class_element_level.advanced,
        Constants.dog_class_element_level.superior,
        Constants.dog_class_element_level.master,
        Constants.dog_class_element_level.elite,
      ],
      [Constants.dog_class_element.nosework_exterior]: [
        Constants.dog_class_element_level.novice,
        Constants.dog_class_element_level.advanced,
        Constants.dog_class_element_level.superior,
        Constants.dog_class_element_level.master,
        Constants.dog_class_element_level.elite,
      ],
      [Constants.dog_class_element.nosework_interior]: [
        Constants.dog_class_element_level.novice,
        Constants.dog_class_element_level.advanced,
        Constants.dog_class_element_level.superior,
        Constants.dog_class_element_level.master,
        Constants.dog_class_element_level.elite,
      ],
      [Constants.dog_class_element.nosework_vehicle]: [
        Constants.dog_class_element_level.novice,
        Constants.dog_class_element_level.advanced,
        Constants.dog_class_element_level.superior,
        Constants.dog_class_element_level.master,
        Constants.dog_class_element_level.elite,
        Constants.dog_class_element_level.excellent,
      ],
      [Constants.dog_class_element.nosework_handler_descrimination]: [
        Constants.dog_class_element_level.novice,
        Constants.dog_class_element_level.advanced,
        Constants.dog_class_element_level.excellent,
        Constants.dog_class_element_level.master,
      ],
    },
    [Constants.show_type.conformation]: {
      [Constants.dog_class_element.conformation]: [
        Constants.dog_class_element_level.puppy,
        Constants.dog_class_element_level.junior,
        Constants.dog_class_element_level.adult,
        Constants.dog_class_element_level.open,
        Constants.dog_class_element_level.breader_handler,
        Constants.dog_class_element_level.champion,
        Constants.dog_class_element_level.grand_champion
      ],
      [Constants.dog_class_element.conformation_altered]: [
        Constants.dog_class_element_level.puppy,
        Constants.dog_class_element_level.junior,
        Constants.dog_class_element_level.adult,
        Constants.dog_class_element_level.open,
        Constants.dog_class_element_level.breader_handler,
        Constants.dog_class_element_level.champion,
        Constants.dog_class_element_level.grand_champion
      ],
      [Constants.dog_class_element.junior_showmanship]: [
        Constants.dog_class_element_level.pee_wee,
        Constants.dog_class_element_level.sub_junior,
        Constants.dog_class_element_level.pre_junior,
        Constants.dog_class_element_level.novice_junior,
        Constants.dog_class_element_level.open_junior,
        Constants.dog_class_element_level.novice_senior,
        Constants.dog_class_element_level.open_senior,
        Constants.dog_class_element_level.total_junior
      ],
      [Constants.dog_class_element.conformation_non_licensed]: [
        Constants.dog_class_element.novice_puppy,
        Constants.dog_class_element.veteran,
        Constants.dog_class_element.brood_bitch_stud_dog,
        Constants.dog_class_element.brace,
        Constants.dog_class_element.total_dog,
        Constants.dog_class_element.total_junior,
        Constants.dog_class_element.other_racing_bred_dogs
      ],
    },
    [Constants.show_type.obedience]: {
      [Constants.dog_class_element_level.pre_novice]: [],
      [Constants.dog_class_element_level.beginner_novice]: [],
      [Constants.dog_class_element_level.novice_a]: [],
      [Constants.dog_class_element_level.novice_b]: [],
      [Constants.dog_class_element_level.novice_c]: [],
      [Constants.dog_class_element_level.advance_novice]: [],
      [Constants.dog_class_element_level.open_a]: [],
      [Constants.dog_class_element_level.open_b]: [],
      [Constants.dog_class_element_level.advance_open]: [],
      [Constants.dog_class_element_level.utility_a]: [],
      [Constants.dog_class_element_level.utility_b]: [],
      [Constants.dog_class_element_level.master]: [],
      [Constants.dog_class_element_level.elite]: [],
      [Constants.dog_class_element_level.uudx]: [],
      [Constants.dog_class_element.conformation_non_licensed]: [
          Constants.dog_class_element_level.brace,
          Constants.dog_class_element_level.pairs,
          Constants.dog_class_element_level.precision_heeling,
          Constants.dog_class_element_level.team,
          Constants.dog_class_element_level.veteran
      ]
    },
    [Constants.show_type.rally_obedience]: {
      // Level RO1/RO2/RO3/ROM with section A/B/C (RO1 only offers C; URX/RMX have no
      // section - see getLevelSections). Shows created before this layout store the
      // class as level "RO1 B"; see ukc-rally-classes.ts for the compatibility helpers.
      [Constants.dog_class_element.performance_rally_obedience]: {
        levels: [
          Constants.dog_class_element_level.ro1,
          Constants.dog_class_element_level.ro2,
          Constants.dog_class_element_level.ro3,
          Constants.dog_class_element_level.rom,
          Constants.dog_class_element_level.urx,
          Constants.dog_class_element_level.rmx,
        ],
        sections: [
          Constants.dog_class_section.ukc_rally_a,
          Constants.dog_class_section.ukc_rally_b,
          Constants.dog_class_section.ukc_rally_c,
        ],
      },
    },
    [Constants.show_type.agility]: {
        [Constants.dog_class_element_level.beginner_a]: [],
        [Constants.dog_class_element_level.beginner_b]: [],
        [Constants.dog_class_element_level.intermediate_a]: [],
        [Constants.dog_class_element_level.intermediate_b]: [],
        [Constants.dog_class_element_level.excellent]: [],
        // GRACH runs the B levels; GRACH A runs the A levels; ACH runs Beginner B
        // and Intermediate B. Legacy "GRACH B" remains in Constants for existing
        // shows but is not selectable.
        [Constants.dog_class_element_level.ach]: [],
        [Constants.dog_class_element_level.grach]: [],
        [Constants.dog_class_element_level.grach_a]: [],
        [Constants.dog_class_element_level.novice_hurdles]: [],
        [Constants.dog_class_element_level.elite_hurdles]: [],
        [Constants.dog_class_element_level.novice_tunnels]: [],
        [Constants.dog_class_element_level.elite_tunnels]: [],
        [Constants.dog_class_element_level.three_of_a_kind]: [],
    },
    [Constants.show_type.weight_pull]: {
      // NOTE: "Weight Pull" (Constants.dog_class_element.performance_weight_pull) is kept
      // out of this list intentionally - it's retired for new shows, but the constant is
      // preserved so historical shows/scores using it continue to read correctly.
      [Constants.dog_class_element.weight_pull_wheels_natural]: [
        Constants.dog_class_element_level.division_a,
        Constants.dog_class_element_level.division_b
      ],
      [Constants.dog_class_element.weight_pull_wheels_artificial]: [
        Constants.dog_class_element_level.division_a,
        Constants.dog_class_element_level.division_b
      ],
      [Constants.dog_class_element.weight_pull_snow]: [
        Constants.dog_class_element_level.division_a,
        Constants.dog_class_element_level.division_b
      ],
      [Constants.dog_class_element.weight_pull_rail]: [
        Constants.dog_class_element_level.division_a,
        Constants.dog_class_element_level.division_b
      ]
    },
    [Constants.show_type.precision_coursing]: {
      [Constants.dog_class_element_level.novice]: [],
      [Constants.dog_class_element_level.advanced]: [],
      [Constants.dog_class_element_level.superior]: [],
      [Constants.dog_class_element_level.master]: [],
      [Constants.dog_class_element_level.elite]: [],
      [Constants.dog_class_element_level.dash]: [],
      [Constants.dog_class_element_level.steeplechase]: [],
      [Constants.dog_class_element_level.fun_run]: [],
      [Constants.dog_class_element_level.puppy_dash]: []
    },
    [Constants.show_type.shed_dog]: {
      [Constants.dog_class_element_level.working_shed_dog]: [],
      [Constants.dog_class_element_level.shed_dog_champion]: [],
      [Constants.dog_class_element_level.shed_dog_youth]: []
    },
    [Constants.show_type.dock_jumping]: {
      [Constants.dog_class_element.dock_jumping_air]: [
        Constants.dog_class_element_level.dock_jumping_splash_1,
        Constants.dog_class_element_level.dock_jumping_splash_2,
        Constants.dog_class_element_level.dock_jumping_splash_3,
        Constants.dog_class_element_level.dock_jumping_splash_4,
        Constants.dog_class_element_level.dock_jumping_splash_5,
        Constants.dog_class_element_level.dock_jumping_splash_6
      ],
      [Constants.dog_class_element.dock_jumping_vertical]: [
        Constants.dog_class_element_level.dock_jumping_vertical
      ],
      [Constants.dog_class_element.dock_jumping_fetch_it]: [
        Constants.dog_class_element_level.dock_jumping_fetch_it
      ],
      [Constants.dog_class_element.dock_jumping_raider_run]: [
        Constants.dog_class_element_level.dock_jumping_raider_run
      ]
    }
  },
  [Constants.sanctioning_club.AKC]: {
    [Constants.show_type.conformation]: {
      [Constants.dog_class_element_level.akc_4_6_month_puppy]: [],
      [Constants.dog_class_element_level.akc_4_6_month_puppy_exhibitor_only]: [],
      [Constants.dog_class_element_level.akc_6_9_month_puppy]: [],
      [Constants.dog_class_element_level.akc_9_12_month_puppy]: [],
      [Constants.dog_class_element_level.akc_12_15_month_puppy]: [],
      [Constants.dog_class_element_level.akc_15_18_month_puppy]: [],
      [Constants.dog_class_element_level.akc_12_18_month_puppy]: [],
      [Constants.dog_class_element_level.akc_18_24_month_puppy]: [],
      [Constants.dog_class_element_level.akc_under_12_month_bred_by_exhibitor]: [],
      [Constants.dog_class_element_level.akc_adult_bred_by_exhibitor]: [],
      [Constants.dog_class_element_level.akc_amateur_owner_handler]: [],
      [Constants.dog_class_element_level.akc_american_bred]: [],
      [Constants.dog_class_element_level.akc_open]: [],
      [Constants.dog_class_element_level.akc_exhibition_only]: [],
      [Constants.dog_class_element_level.akc_novice]: [],
      [Constants.dog_class_element_level.akc_miscellaneous]: [],
      [Constants.dog_class_element_level.akc_best_of_breed]: [],
      [Constants.dog_class_element_level.akc_brood_bitch]: [],
      [Constants.dog_class_element_level.akc_stud_dog]: [],
      [Constants.dog_class_element_level.akc_brace]: [],
      [Constants.dog_class_element_level.akc_veteran_6_8_years]: [],
      [Constants.dog_class_element_level.akc_veteran_6_plus_years]: [],
      [Constants.dog_class_element_level.akc_veteran_8_10_years]: [],
      [Constants.dog_class_element_level.akc_veteran_8_11_years]: [],
      [Constants.dog_class_element_level.akc_veteran_10_12_years]: [],
      [Constants.dog_class_element_level.akc_veteran_11_plus_years]: [],
      [Constants.dog_class_element_level.akc_veteran_7_plus_years]: [],
      [Constants.dog_class_element_level.akc_veteran_8_plus_years]: [],
      [Constants.dog_class_element_level.akc_veteran_10_plus_years]: [],
      [Constants.dog_class_element_level.akc_veteran_13_plus_years]: [],
      [Constants.dog_class_element_level.akc_veteran_7_10_years]: [],
      [Constants.dog_class_element_level.akc_veteran_7_9_years]: [],
      [Constants.dog_class_element_level.akc_veteran_9_plus_years]: [],
      [Constants.dog_class_element_level.akc_veteran_12_plus_years]: [],
      [Constants.dog_class_element.conformation_non_licensed]: [
        // REMOVED: akc_puppy_sweepstakes is deprecated - use age-specific classes instead
        Constants.dog_class_element_level.akc_puppy_sweepstakes_6_9,
        Constants.dog_class_element_level.akc_puppy_sweepstakes_9_12,
        Constants.dog_class_element_level.akc_puppy_sweepstakes_12_18,
        Constants.dog_class_element_level.akc_puppy_sweepstakes_12_15,
        Constants.dog_class_element_level.akc_puppy_sweepstakes_15_18,
        Constants.dog_class_element_level.akc_puppy_sweepstakes_bred_by_exhibitor_6_18,
        Constants.dog_class_element_level.akc_puppy_sweepstakes_18_24,
        Constants.dog_class_element_level.akc_veteran_sweepstakes_7_9,
        Constants.dog_class_element_level.akc_veteran_sweepstakes_7_10,
        Constants.dog_class_element_level.akc_veteran_sweepstakes_8_10,
        Constants.dog_class_element_level.akc_veteran_sweepstakes_8_11,
        Constants.dog_class_element_level.akc_veteran_sweepstakes_9_plus,
        Constants.dog_class_element_level.akc_veteran_sweepstakes_10_12,
        Constants.dog_class_element_level.akc_veteran_sweepstakes_11_plus,
        Constants.dog_class_element_level.akc_veteran_sweepstakes_10_13,
        Constants.dog_class_element_level.akc_veteran_sweepstakes_10_plus,
        Constants.dog_class_element_level.akc_veteran_sweepstakes_12_plus,
        Constants.dog_class_element_level.akc_veteran_sweepstakes_13_plus,
        Constants.dog_class_element_level.akc_dual_title_sweepstakes_6m_7y,
        Constants.dog_class_element_level.akc_dual_title_sweepstakes_7y_plus,
        Constants.dog_class_element_level.akc_dual_title_sweepstakes_7y_11y,
        Constants.dog_class_element_level.akc_dual_title_sweepstakes_10y_plus,
        Constants.dog_class_element_level.akc_dual_title_sweepstakes_11y_plus,
        Constants.dog_class_element_level.akc_sled_dog,
        Constants.dog_class_element_level.akc_hunting_class,
        Constants.dog_class_element_level.akc_field_trial_class,
        Constants.dog_class_element_level.akc_teams,
        Constants.dog_class_element_level.akc_parade_of_titleholders,
        Constants.dog_class_element_level.akc_coursing,
        Constants.dog_class_element_level.akc_best_head,
        Constants.dog_class_element_level.akc_best_movement,
        Constants.dog_class_element_level.akc_best_field,
        // Junior Showmanship classes (selectable individually)
        Constants.dog_class_element_level.akc_novice_junior,
        Constants.dog_class_element_level.akc_novice_intermediate,
        Constants.dog_class_element_level.akc_novice_senior,
        Constants.dog_class_element_level.akc_open_junior,
        Constants.dog_class_element_level.akc_open_intermediate,
        Constants.dog_class_element_level.akc_open_senior,
        Constants.dog_class_element_level.akc_master_class,
        Constants.dog_class_element_level.akc_junior_showmanship_novice,
        Constants.dog_class_element_level.akc_junior_showmanship_open,
        Constants.dog_class_element_level.akc_junior_showmanship_master,
      ],
    },
    [Constants.show_type.obedience]: {
      [Constants.dog_class_element_level.beginner_novice_a]: [],
      [Constants.dog_class_element_level.beginner_novice_b]: [],
      [Constants.dog_class_element_level.preferred_novice]: [],
      [Constants.dog_class_element_level.graduate_novice]: [],
      [Constants.dog_class_element_level.novice_a]: [],
      [Constants.dog_class_element_level.novice_b]: [],
      [Constants.dog_class_element_level.open_a]: [],
      [Constants.dog_class_element_level.open_b]: [],
      [Constants.dog_class_element_level.preferred_open]: [],
      [Constants.dog_class_element_level.graduate_open]: [],
      [Constants.dog_class_element_level.preferred_graduate_open]: [],
      [Constants.dog_class_element_level.utility_a]: [],
      [Constants.dog_class_element_level.utility_b]: [],
      [Constants.dog_class_element_level.preferred_utility]: [],
      [Constants.dog_class_element_level.versatility]: [],
      [Constants.dog_class_element_level.veteran]: [],
      [Constants.dog_class_element_level.brace]: [],
      [Constants.dog_class_element_level.team]: [],
      [Constants.dog_class_element_level.wc_novice]: [],
      [Constants.dog_class_element_level.wc_open]: [],
      [Constants.dog_class_element_level.wc_utility]: [],
      [Constants.dog_class_element_level.udx]: [],
      [Constants.dog_class_element_level.random_draw_novice]: [],
      [Constants.dog_class_element_level.random_draw_open]: [],
      [Constants.dog_class_element_level.random_reward_utility]: []
    },
    [Constants.show_type.rally_obedience]: {
      [Constants.dog_class_element_level.novice_a]: [],
      [Constants.dog_class_element_level.novice_b]: [],
      [Constants.dog_class_element_level.intermediate]: [],
      [Constants.dog_class_element_level.advanced_a]: [],
      [Constants.dog_class_element_level.advanced_b]: [],
      [Constants.dog_class_element_level.excellent_a]: [],
      [Constants.dog_class_element_level.excellent_b]: [],
      [Constants.dog_class_element_level.master]: [],
      [Constants.dog_class_element_level.choice]: [],
      [Constants.dog_class_element_level.pairs]: [],
      [Constants.dog_class_element_level.novice_pairs]: [],
      [Constants.dog_class_element_level.advanced_pairs]: [],
      [Constants.dog_class_element_level.excellent_pairs]: [],
      [Constants.dog_class_element_level.team]: [],
      [Constants.dog_class_element_level.novice_teams]: [],
      [Constants.dog_class_element_level.advanced_teams]: [],
      [Constants.dog_class_element_level.rae]: [],
      [Constants.dog_class_element_level.rach]: []
    },
    [Constants.show_type.fast_cat]: {
      [Constants.dog_class_element.fast_cat]: [
        Constants.dog_class_element.fast_cat
      ]
    },
    [Constants.show_type.cat]: {
      [Constants.dog_class_element.cat]: [
        Constants.dog_class_element.cat
      ]
    },
    [Constants.show_type.canine_good_citizen]: {
      [Constants.dog_class_element.canine_good_citizen]: [],
      [Constants.dog_class_element.canine_good_citizen_advanced]: [],
      [Constants.dog_class_element.canine_good_citizen_urban]: []
    },
    [Constants.show_type.trick_dog]: {
      [Constants.dog_class_element_level.tkn]: [],
      [Constants.dog_class_element_level.tki]: [],
      [Constants.dog_class_element_level.tka]: [],
      [Constants.dog_class_element_level.tkp]: [],
      [Constants.dog_class_element_level.tke]: []
    },
    [Constants.show_type.fetch]: {
      [Constants.dog_class_element_level.novice]: [],
      [Constants.dog_class_element_level.intermediate]: [],
      [Constants.dog_class_element_level.advanced]: [],
      [Constants.dog_class_element_level.retriever]: []
    },
    [Constants.show_type.scentwork]: {
      [Constants.dog_class_element.scentwork_container]: [
        Constants.dog_class_element_level.novice_a,
        Constants.dog_class_element_level.novice_b,
        Constants.dog_class_element_level.scentwork_advanced,
        Constants.dog_class_element_level.scentwork_excellent,
        Constants.dog_class_element_level.scentwork_master
      ],
      [Constants.dog_class_element.scentwork_interior]: [
        Constants.dog_class_element_level.novice_a,
        Constants.dog_class_element_level.novice_b,
        Constants.dog_class_element_level.scentwork_advanced,
        Constants.dog_class_element_level.scentwork_excellent,
        Constants.dog_class_element_level.scentwork_master
      ],
      [Constants.dog_class_element.scentwork_exterior]: [
        Constants.dog_class_element_level.novice_a,
        Constants.dog_class_element_level.novice_b,
        Constants.dog_class_element_level.scentwork_advanced,
        Constants.dog_class_element_level.scentwork_excellent,
        Constants.dog_class_element_level.scentwork_master
      ],
      [Constants.dog_class_element.scentwork_buried]: [
        Constants.dog_class_element_level.novice_a,
        Constants.dog_class_element_level.novice_b,
        Constants.dog_class_element_level.scentwork_advanced,
        Constants.dog_class_element_level.scentwork_excellent,
        Constants.dog_class_element_level.scentwork_master
      ],
      [Constants.dog_class_element.scentwork_handler_disc]: [
        Constants.dog_class_element_level.novice_a,
        Constants.dog_class_element_level.novice_b,
        Constants.dog_class_element_level.scentwork_advanced,
        Constants.dog_class_element_level.scentwork_excellent,
        Constants.dog_class_element_level.scentwork_master
      ],
      [Constants.dog_class_element.scentwork_detective]: [
        Constants.dog_class_element_level.scentwork_detective
      ]
    },
    [Constants.show_type.lure_coursing]: {
      [Constants.dog_class_element_level.akc_open]: [],
      [Constants.dog_class_element_level.special]: [],
      [Constants.dog_class_element_level.veteran]: [],
      [Constants.dog_class_element_level.junior_coursing]: [],
      [Constants.dog_class_element_level.qualified_coursing]: []
    },
    [Constants.show_type.farm_dog]: {
      [Constants.dog_class_element.farm_dog]: [
        Constants.dog_class_element.farm_dog
      ]
    },
    [Constants.show_type.att]: {
      [Constants.dog_class_element.att]: [
        Constants.dog_class_element.att
      ]
    },
    // Each course crosses every level (Started/Intermediate/Advance) with every
    // stock type (Sheep/Cattle/Ducks), so this must use the {levels, sections}
    // config object - a plain array (as used by Pre-Trial/Instinct Test below,
    // where stock type IS the level) would collapse all stock types into a
    // single section-less class per course/level. See
    // data/prd/AKC_HERDING_ALTERNATES_PRD.md and AKC_HERDING_ARMBAND_GENERATION_PRD.md.
    [Constants.show_type.herding]: {
      [Constants.dog_class_element.course_a]: {
        levels: [
          Constants.dog_class_element_level.started,
          Constants.dog_class_element_level.intermediate,
          Constants.dog_class_element_level.advance,
        ],
        sections: [
          Constants.dog_class_section.sheep,
          Constants.dog_class_section.cattle,
          Constants.dog_class_section.ducks,
        ],
      },
      [Constants.dog_class_element.course_b]: {
        levels: [
          Constants.dog_class_element_level.started,
          Constants.dog_class_element_level.intermediate,
          Constants.dog_class_element_level.advance,
        ],
        sections: [
          Constants.dog_class_section.sheep,
          Constants.dog_class_section.cattle,
          Constants.dog_class_section.ducks,
        ],
      },
      [Constants.dog_class_element.course_c]: {
        levels: [
          Constants.dog_class_element_level.started,
          Constants.dog_class_element_level.intermediate,
          Constants.dog_class_element_level.advance,
        ],
        sections: [
          Constants.dog_class_section.sheep,
          Constants.dog_class_section.cattle,
          Constants.dog_class_section.ducks,
        ],
      },
      [Constants.dog_class_element.course_d]: {
        levels: [
          Constants.dog_class_element_level.started,
          Constants.dog_class_element_level.intermediate,
          Constants.dog_class_element_level.advance,
        ],
        sections: [
          Constants.dog_class_section.sheep,
          Constants.dog_class_section.cattle,
          Constants.dog_class_section.ducks,
        ],
      },
    },
    [Constants.show_type.pre_trial_herding]: {
      [Constants.show_type.pre_trial_herding]: [
        Constants.dog_class_section.sheep,
        Constants.dog_class_section.cattle,
        Constants.dog_class_section.ducks,
      ],
    },
    [Constants.show_type.instinct_test_herding]: {
      [Constants.show_type.instinct_test_herding]: [
        Constants.dog_class_section.sheep,
        Constants.dog_class_section.cattle,
        Constants.dog_class_section.ducks,
      ],
    },
    [Constants.show_type.agility]: {
      [Constants.dog_class_element.standard]: [
        Constants.dog_class_element_level.novice_a,
        Constants.dog_class_element_level.novice_b,
        Constants.dog_class_element_level.novice_preferred,
        Constants.dog_class_element_level.akc_open,
        Constants.dog_class_element_level.open_preferred,
        Constants.dog_class_element_level.excellent,
        Constants.dog_class_element_level.excellent_preferred,
        Constants.dog_class_element_level.master,
        Constants.dog_class_element_level.master_preferred,
      ],
      [Constants.dog_class_element.jumpers_with_weaves]: [
        Constants.dog_class_element_level.novice_a,
        Constants.dog_class_element_level.novice_b,
        Constants.dog_class_element_level.novice_preferred,
        Constants.dog_class_element_level.akc_open,
        Constants.dog_class_element_level.open_preferred,
        Constants.dog_class_element_level.excellent,
        Constants.dog_class_element_level.excellent_preferred,
        Constants.dog_class_element_level.master,
        Constants.dog_class_element_level.master_preferred,
      ],
      [Constants.dog_class_element.fast]: [
        Constants.dog_class_element_level.novice_a,
        Constants.dog_class_element_level.novice_b,
        Constants.dog_class_element_level.novice_preferred,
        Constants.dog_class_element_level.akc_open,
        Constants.dog_class_element_level.open_preferred,
        Constants.dog_class_element_level.excellent,
        Constants.dog_class_element_level.excellent_preferred,
        Constants.dog_class_element_level.master,
        Constants.dog_class_element_level.master_preferred,
      ],
      [Constants.dog_class_element.t2b]: [
        Constants.dog_class_element_level.t2b,
        Constants.dog_class_element_level.t2b_preferred,
      ],
      [Constants.dog_class_element.premier_standard]: [
        Constants.dog_class_element_level.premier_standard,
        Constants.dog_class_element_level.premier_standard_preferred,
      ],
      [Constants.dog_class_element.premier_jww]: [
        Constants.dog_class_element_level.premier_jww,
        Constants.dog_class_element_level.premier_jww_preferred,
      ],
    }
  },
  [Constants.sanctioning_club.ABI]: {
    [Constants.show_type.conformation]: {
      [Constants.dog_class_element.conformation]: [
        Constants.dog_class_element_level.abi_6_9_months,
        Constants.dog_class_element_level.abi_9_12_months,
        Constants.dog_class_element_level.abi_12_18_months,
        Constants.dog_class_element_level.abi_bred_by_exibitor,
        Constants.dog_class_element_level.abi_open,
        //Constants.dog_class_element_level.abi_best_of_breed,
        Constants.dog_class_element_level.abi_brace,
        Constants.dog_class_element_level.abi_veteran,
        Constants.dog_class_element_level.abi_jr_champion,
        Constants.dog_class_element_level.abi_champion,
        Constants.dog_class_element_level.abi_master_champion,
        Constants.dog_class_element_level.abi_grand_champion,
        Constants.dog_class_element_level.abi_supreme_champion,
        Constants.dog_class_element_level.abi_ultimate_champion,
        Constants.dog_class_element_level.abi_royal_champion,
        Constants.dog_class_element_level.abi_legendary_champion,
      ],
      [Constants.dog_class_element.conformation_altered]: [
        Constants.dog_class_element_level.abi_altered
      ],
      [Constants.dog_class_element.conformation_non_licensed]: [
        Constants.dog_class_element_level.abi_baby_puppy,
        Constants.dog_class_element.veteran,
        Constants.dog_class_element.brace,
        Constants.dog_class_element.abi_stud_dog,
        Constants.dog_class_element.abi_brood_bitch,
        Constants.dog_class_element.abi_star,
        Constants.dog_class_element.abi_canadian_bred_bis,
        Constants.dog_class_element.abi_bred_by_bis,
        Constants.dog_class_element.abi_show_of_shows
      ]
    },
    [Constants.show_type.urban_rat_race]: {
      [Constants.dog_class_element.tubes]: [
        Constants.dog_class_element_level.rookie,
        Constants.dog_class_element_level.clever,
        Constants.dog_class_element_level.adventurer,
        Constants.dog_class_element_level.explorer,
        Constants.dog_class_element_level.detective,
        Constants.dog_class_element_level.investigator
      ],
      [Constants.dog_class_element.unobstructed]: [
        Constants.dog_class_element_level.rookie,
        Constants.dog_class_element_level.clever,
        Constants.dog_class_element_level.adventurer,
        Constants.dog_class_element_level.explorer,
        Constants.dog_class_element_level.detective,
        Constants.dog_class_element_level.investigator
      ],
      [Constants.dog_class_element.obstructed]: [
        Constants.dog_class_element_level.rookie,
        Constants.dog_class_element_level.clever,
        Constants.dog_class_element_level.adventurer,
        Constants.dog_class_element_level.explorer,
        Constants.dog_class_element_level.detective,
        Constants.dog_class_element_level.investigator
      ],
      [Constants.dog_class_element.obstructed_2]: [
        Constants.dog_class_element_level.rookie,
        Constants.dog_class_element_level.clever,
        Constants.dog_class_element_level.adventurer,
        Constants.dog_class_element_level.explorer,
        Constants.dog_class_element_level.detective,
        Constants.dog_class_element_level.investigator
      ],
      [Constants.dog_class_element.games]: [
        Constants.dog_class_element_level.fantastic_five,
        Constants.dog_class_element_level.tempting_ten,
        Constants.dog_class_element_level.junkyard,
        Constants.dog_class_element_level.junkyard_ex,
        Constants.dog_class_element_level.tag_team,
      ],
    },
    [Constants.show_type.country_rat_race]: {
      [Constants.dog_class_element_level.cubs]: [],
      [Constants.dog_class_element_level.scouts]: [],
      [Constants.dog_class_element_level.huntsman]: [],
      [Constants.dog_class_element_level.huntsmaster]: [],
      [Constants.dog_class_element_level.field_master]: []
    },
    [Constants.show_type.scent_dog_games]: {
      [Constants.dog_class_element.performance_scent_dog_games]: {
        levels: [
          Constants.dog_class_element_level.sdg_once_around,
          Constants.dog_class_element_level.sdg_double_or_nothing,
          Constants.dog_class_element_level.sdg_clear_it,
          Constants.dog_class_element_level.sdg_duo,
          Constants.dog_class_element_level.sdg_teams,
          Constants.dog_class_element_level.sdg_against_odds,
          Constants.dog_class_element_level.sdg_hi_lo,
          Constants.dog_class_element_level.sdg_double_0_7,
          Constants.dog_class_element_level.sdg_at_distance,
          Constants.dog_class_element_level.sdg_relay_race,
          Constants.dog_class_element_level.sdg_cluster,
          Constants.dog_class_element_level.sdg_hit_the_wall,
          Constants.dog_class_element_level.sdg_twice_around_foundations,
          Constants.dog_class_element_level.sdg_clear_it_foundations,
          Constants.dog_class_element_level.sdg_cluster_foundations,
          Constants.dog_class_element_level.sdg_hi_lo_foundations,
          Constants.dog_class_element_level.sdg_hit_the_wall_foundations,
        ],
        sections: [
          Constants.dog_class_section.sdg_level_1,
          Constants.dog_class_section.sdg_level_2,
        ],
      },
    },
    [Constants.show_type.lost_and_found]: {
      [Constants.dog_class_element.lf_containers]: [
        Constants.dog_class_element_level.seeker,
        Constants.dog_class_element_level.locater,
        Constants.dog_class_element_level.discover,
      ],
      [Constants.dog_class_element.lf_interiors]: [
        Constants.dog_class_element_level.seeker,
        Constants.dog_class_element_level.locater,
        Constants.dog_class_element_level.discover,
      ],
      [Constants.dog_class_element.lf_exteriors]: [
        Constants.dog_class_element_level.seeker,
        Constants.dog_class_element_level.locater,
        Constants.dog_class_element_level.discover,
      ],
    },
  },
  [Constants.sanctioning_club.ISWS]: {
    [Constants.show_type.conformation]: {
      [Constants.dog_class_element.conformation]: [
        // Constants.dog_class_element_level.isws_puppy_3_6, // Moved to non-regular classes
        Constants.dog_class_element_level.isws_puppy_6_9,
        Constants.dog_class_element_level.isws_puppy_6_12,
        Constants.dog_class_element_level.isws_puppy_9_12,
        Constants.dog_class_element_level.isws_junior_puppy,
        Constants.dog_class_element_level.isws_senior_puppy,
        Constants.dog_class_element_level.isws_1218,
        Constants.dog_class_element_level.isws_novice,
        Constants.dog_class_element_level.isws_canadian_bred,
        Constants.dog_class_element_level.isws_american_bred,
        Constants.dog_class_element_level.isws_bred_by_exibitor,
        Constants.dog_class_element_level.isws_open,
        Constants.dog_class_element_level.isws_best_of_breed,
        Constants.dog_class_element_level.isws_field_performance_champion,
        // Constants.dog_class_element_level.isws_performance_champion, // Hidden for now
        // Constants.dog_class_element_level.isws_champion, // Hidden for now
      ],
      [Constants.dog_class_element.conformation_altered]: [
        Constants.dog_class_element_level.isws_altered_novice,
        Constants.dog_class_element_level.isws_altered_open,
        Constants.dog_class_element_level.isws_altered_champion,
      ],
      [Constants.dog_class_element.conformation_non_regular]: [
        Constants.dog_class_element_level.isws_puppy_3_6, // Moved from regular classes
        Constants.dog_class_element_level.isws_veteran,
        Constants.dog_class_element_level.isws_veteran_7_10,
        Constants.dog_class_element_level.isws_veteran_10_plus,
        Constants.dog_class_element_level.isws_veteran_10_13,
        Constants.dog_class_element_level.isws_veteran_13_plus,
      ],
      [Constants.dog_class_element.conformation_sweepstakes]: [
        Constants.dog_class_element_level.isws_sweeps_puppy_3_6,
        Constants.dog_class_element_level.isws_sweeps_puppy_6_12,
        Constants.dog_class_element_level.isws_sweeps_12_24,
        Constants.dog_class_element_level.isws_sweeps_veteran_7_plus,
        Constants.dog_class_element_level.isws_sweeps_veteran_7_10,
        Constants.dog_class_element_level.isws_sweeps_veteran_10_plus,
        Constants.dog_class_element_level.isws_sweeps_veteran_7_9,
        Constants.dog_class_element_level.isws_sweeps_veteran_9_13,
        Constants.dog_class_element_level.isws_sweeps_veteran_13_plus,
      ],
      [Constants.dog_class_element.conformation_junior]: [
        Constants.dog_class_element_level.isws_junior_handler_1,
        Constants.dog_class_element_level.isws_junior_handler_2,
        Constants.dog_class_element_level.isws_junior_showmanship_1,
        Constants.dog_class_element_level.isws_junior_showmanship_2,
      ],
      [Constants.dog_class_element.conformation_special_classes]: [
        Constants.dog_class_element_level.isws_brace,
        Constants.dog_class_element_level.isws_stud_dog,
        Constants.dog_class_element_level.isws_brood_bitch,
      ]
    },
    [Constants.show_type.fast_dash]: {
      [Constants.dog_class_element.fast_dash]: [
        Constants.dog_class_element.fast_dash
      ]
    }
  },
  [Constants.sanctioning_club.CKC]: {
    [Constants.show_type.conformation]: {
      [Constants.dog_class_element.conformation]: [
        Constants.dog_class_element_level.ckc_junior_puppy,
        Constants.dog_class_element_level.ckc_senior_puppy,
        Constants.dog_class_element_level.ckc_12_18_month,
        Constants.dog_class_element_level.ckc_canadian_bred,
        Constants.dog_class_element_level.ckc_bred_by_exhibitor,
        Constants.dog_class_element_level.ckc_open,
        Constants.dog_class_element_level.ckc_specials_only,
      ],
      [Constants.dog_class_element.conformation_non_regular]: [
        Constants.dog_class_element_level.ckc_baby_puppy,
        Constants.dog_class_element_level.ckc_veterans,
        Constants.dog_class_element_level.ckc_exhibition_only,
      ],
      [Constants.dog_class_element.conformation_special_classes]: [
        Constants.dog_class_element_level.ckc_brace,
      ],
      [Constants.dog_class_element.conformation_sweepstakes]: [
        Constants.dog_class_element_level.ckc_sweepstakes_4_6,
        Constants.dog_class_element_level.ckc_sweepstakes_6_9,
        Constants.dog_class_element_level.ckc_sweepstakes_9_12,
        Constants.dog_class_element_level.ckc_sweepstakes_12_18,
        Constants.dog_class_element_level.ckc_veterans_sweepstakes,
      ],
      [Constants.dog_class_element.conformation_altered]: [
        Constants.dog_class_element_level.ckc_altered,
      ],
    },
    // CKC Obedience Trial Rules 2.1.5 (official classes) and Chapter 16 (unofficial classes)
    [Constants.show_type.obedience]: {
      [Constants.dog_class_element_level.pre_novice]: [],
      [Constants.dog_class_element_level.novice_a]: [],
      [Constants.dog_class_element_level.novice_b]: [],
      [Constants.dog_class_element_level.novice_c]: [],
      [Constants.dog_class_element_level.ckc_novice_intermediate]: [],
      [Constants.dog_class_element_level.open_a]: [],
      [Constants.dog_class_element_level.open_b]: [],
      [Constants.dog_class_element_level.utility_a]: [],
      [Constants.dog_class_element_level.utility_b]: [],
      [Constants.dog_class_element_level.ckc_exhibition_only]: [],
      [Constants.dog_class_element.obedience_unofficial]: [
        Constants.dog_class_element_level.wc_novice,
        Constants.dog_class_element_level.wc_open,
        Constants.dog_class_element_level.wc_utility,
        Constants.dog_class_element_level.brace,
        Constants.dog_class_element_level.team,
        Constants.dog_class_element_level.veteran,
        Constants.dog_class_element_level.graduate_novice,
        Constants.dog_class_element_level.graduate_open,
        Constants.dog_class_element_level.ckc_pre_open,
        Constants.dog_class_element_level.ckc_pre_utility,
      ],
    },
    // CKC Rally Obedience Trial Rules 2.1.3 (official classes), 18.1 (Exhibition Only), 19.2-19.3 (Brace, Team)
    [Constants.show_type.rally_obedience]: {
      [Constants.dog_class_element_level.novice_a]: [],
      [Constants.dog_class_element_level.novice_b]: [],
      [Constants.dog_class_element_level.intermediate]: [],
      [Constants.dog_class_element_level.advanced_a]: [],
      [Constants.dog_class_element_level.advanced_b]: [],
      [Constants.dog_class_element_level.excellent_a]: [],
      [Constants.dog_class_element_level.excellent_b]: [],
      [Constants.dog_class_element_level.master]: [],
      [Constants.dog_class_element_level.ckc_exhibition_only]: [],
      [Constants.dog_class_element.rally_unofficial]: [
        Constants.dog_class_element_level.brace,
        Constants.dog_class_element_level.team,
      ],
    },
    // CKC Scent Detection Trial Rules 6.8.1 (classes), 6.8.2 (components); Instinct is Container only (9.6.2)
    [Constants.show_type.scent_detection]: {
      [Constants.dog_class_element.scent_detection_container]: [
        Constants.dog_class_element_level.scent_detection_instinct,
        Constants.dog_class_element_level.scent_detection_novice,
        Constants.dog_class_element_level.scent_detection_open,
        Constants.dog_class_element_level.scent_detection_excellent,
        Constants.dog_class_element_level.scent_detection_master,
      ],
      [Constants.dog_class_element.scent_detection_interior]: [
        Constants.dog_class_element_level.scent_detection_novice,
        Constants.dog_class_element_level.scent_detection_open,
        Constants.dog_class_element_level.scent_detection_excellent,
        Constants.dog_class_element_level.scent_detection_master,
      ],
      [Constants.dog_class_element.scent_detection_exterior]: [
        Constants.dog_class_element_level.scent_detection_novice,
        Constants.dog_class_element_level.scent_detection_open,
        Constants.dog_class_element_level.scent_detection_excellent,
        Constants.dog_class_element_level.scent_detection_master,
      ],
    },
  },
  [Constants.sanctioning_club.PWDCA]: {
    [Constants.show_type.water_dog]: {
      [Constants.dog_class_element.junior_water_dog]: [],
      [Constants.dog_class_element.apprentice]: [],
      [Constants.dog_class_element.working]: [],
      [Constants.dog_class_element.courier]: [],
      [Constants.dog_class_element.master]: [],
    },
  },
  [Constants.sanctioning_club.CDSP]: {
    [Constants.show_type.cdsp_video]: {
      [Constants.dog_class_element.cdsp_starter_novice]: {
        levels: [
          Constants.dog_class_element_level.cdsp_level_a,
          Constants.dog_class_element_level.cdsp_level_b,
          Constants.dog_class_element_level.cdsp_level_c,
        ],
      },
      [Constants.dog_class_element.cdsp_novice]: {
        levels: [
          Constants.dog_class_element_level.cdsp_level_a,
          Constants.dog_class_element_level.cdsp_level_b,
          Constants.dog_class_element_level.cdsp_level_c,
        ],
      },
      [Constants.dog_class_element.cdsp_open]: {
        levels: [
          Constants.dog_class_element_level.cdsp_level_a,
          Constants.dog_class_element_level.cdsp_level_b,
          Constants.dog_class_element_level.cdsp_level_c,
        ],
      },
      [Constants.dog_class_element.cdsp_utility]: {
        levels: [
          Constants.dog_class_element_level.cdsp_level_a,
          Constants.dog_class_element_level.cdsp_level_b,
          Constants.dog_class_element_level.cdsp_level_c,
        ],
      },
      [Constants.dog_class_element.cdsp_versatility]: {
        levels: [
          Constants.dog_class_element_level.cdsp_level_a,
          Constants.dog_class_element_level.cdsp_level_b,
          Constants.dog_class_element_level.cdsp_level_c,
        ],
      },
    },
  },
  [Constants.sanctioning_club.CPE]: {
    [Constants.show_type.speedway]: {
      [Constants.dog_class_element.speedway]: []
    },
  },
  [Constants.sanctioning_club.GCR]: {
    [Constants.show_type.nitrous_50]: {
      [Constants.dog_class_element.nitrous_50]: []
    },
    [Constants.show_type.nitrous_100]: {
      [Constants.dog_class_element.nitrous_100]: []
    },
  },
  [Constants.sanctioning_club.ASFA]: {
    [Constants.show_type.field_trial]: {
      [Constants.dog_class_element.asfa_stakes]: [
        Constants.dog_class_element_level.asfa_open,
        Constants.dog_class_element_level.asfa_fch,
        Constants.dog_class_element_level.asfa_veteran,
        Constants.dog_class_element_level.asfa_singles,
        Constants.dog_class_element_level.asfa_provisional,
      ],
      [Constants.dog_class_element.asfa_additional_stakes]: [
        Constants.dog_class_element_level.asfa_kennel,
        Constants.dog_class_element_level.asfa_breeder,
        Constants.dog_class_element_level.asfa_bench,
      ],
    },
  },
  [Constants.sanctioning_club.GENERAL]: {
    [Constants.show_type.sniff_n_go]: {
      [Constants.dog_class_element.sniff_n_go]: [
        Constants.dog_class_element_level.one_hide_birch,
        Constants.dog_class_element_level.two_hides_birch_anise,
        Constants.dog_class_element_level.three_hides_birch_anise_clove,
        Constants.dog_class_element_level.new_to_nosework
      ]
    },
    [Constants.show_type.dock_diving]: {
      [Constants.dog_class_element.dock_diving]: [
        Constants.dog_class_element.dock_diving
      ]
    },
    [Constants.show_type.on_leash_run_through]: {
      [Constants.dog_class_element_level.on_leash_run_through_novice]: [],
      [Constants.dog_class_element_level.on_leash_run_through_intermediate]: [],
    },
    [Constants.show_type.obedience_show_n_go]: {
      [Constants.dog_class_element_level.obedience_show_n_go_beginner_novice]: [],
      [Constants.dog_class_element_level.obedience_show_n_go_novice]: [],
      [Constants.dog_class_element_level.obedience_show_n_go_open]: [],
      [Constants.dog_class_element_level.obedience_show_n_go_utility]: [],
    },
    [Constants.show_type.general_rally]: {
      [Constants.dog_class_element_level.general_rally_novice]: [],
      [Constants.dog_class_element_level.general_rally_intermediate]: [],
      [Constants.dog_class_element_level.general_rally_advanced]: [],
      [Constants.dog_class_element_level.general_rally_excellent]: [],
      [Constants.dog_class_element_level.general_rally_master]: [],
      [Constants.dog_class_element_level.general_rally_choice]: [],
    },
  },
  [Constants.sanctioning_club.C_WAGS]: {
    [Constants.show_type.scent]: {
      [Constants.dog_class_element.scent]: [
        Constants.dog_class_element_level.patrols_1,
        Constants.dog_class_element_level.detective_2,
        Constants.dog_class_element_level.investigator_3,
        Constants.dog_class_element_level.super_sleuth_4,
        Constants.dog_class_element_level.private_investigator,
        Constants.dog_class_element_level.detective_diversions
      ],
      [Constants.dog_class_element.ranger]: [
        Constants.dog_class_element_level.scent_ranger_1,
        Constants.dog_class_element_level.scent_ranger_2,
        Constants.dog_class_element_level.scent_ranger_3,
        Constants.dog_class_element_level.scent_ranger_4,
        Constants.dog_class_element_level.scent_ranger_5
      ],
      [Constants.dog_class_element.dasher]: [
        Constants.dog_class_element_level.dasher_scent_3,
        Constants.dog_class_element_level.dasher_scent_4,
        Constants.dog_class_element_level.dasher_scent_5,
        Constants.dog_class_element_level.dasher_scent_6
      ]
    },
    [Constants.show_type.obedience]: {
      [Constants.dog_class_element.performance_obedience]: [
        Constants.dog_class_element_level.cwags_obedience_level_1,
        Constants.dog_class_element_level.cwags_obedience_level_2,
        Constants.dog_class_element_level.cwags_obedience_level_3,
        Constants.dog_class_element_level.cwags_obedience_level_4,
        Constants.dog_class_element_level.cwags_obedience_level_5,
      ]
    },
    [Constants.show_type.rally_obedience]: {
      [Constants.dog_class_element.performance_rally_obedience]: [
        Constants.dog_class_element_level.cwags_rally_starter,
        Constants.dog_class_element_level.cwags_rally_advanced,
        Constants.dog_class_element_level.cwags_rally_pro,
        Constants.dog_class_element_level.cwags_rally_zoom_1,
        Constants.dog_class_element_level.cwags_rally_zoom_1_5,
        Constants.dog_class_element_level.cwags_rally_zoom_2,
        Constants.dog_class_element_level.cwags_rally_arf,
      ]
    },
    // C-WAGS Games: one show type, each game is an element, Level 1-4 (+ Zoom
    // variants for the rally-based games) are levels. No sections — the rules
    // state Games classes have no A/B/Jr/TO divisions. Matches C-WAGS' own
    // Trial Results workbook ("Games" tab, classes "Games 1".."Games 4", the
    // game passed is the result code). See data/prd/C-WAGS_GAMES_SHOW_TYPE_PRD.md.
    [Constants.show_type.cwags_games]: {
      [Constants.dog_class_element.cwags_games_colors]: CWAGS_GAMES_RALLY_LEVELS,
      [Constants.dog_class_element.cwags_games_black_jack]: CWAGS_GAMES_RALLY_LEVELS,
      [Constants.dog_class_element.cwags_games_pairs]: CWAGS_GAMES_RALLY_LEVELS,
      [Constants.dog_class_element.cwags_games_team]: CWAGS_GAMES_RALLY_LEVELS,
      // Grab Bag Games rules (2013): Levels 1-3 only, no Zoom.
      [Constants.dog_class_element.cwags_games_grab_bag]: CWAGS_GAMES_GRAB_BAG_LEVELS,
    },
  },
  [Constants.sanctioning_club.BHA]: {
    [Constants.show_type.barn_hunt]: {
      [Constants.dog_class_element_level.barn_hunt_instinct]: [],
      [Constants.dog_class_element_level.barn_hunt_novice]: [],
      [Constants.dog_class_element_level.barn_hunt_open]: [],
      [Constants.dog_class_element_level.barn_hunt_senior]: [],
      [Constants.dog_class_element_level.barn_hunt_master]: [],
      [Constants.dog_class_element_level.barn_hunt_crazy_8s]: [],
      [Constants.dog_class_element_level.barn_hunt_line_drive]: [],
    },
  },
  [Constants.sanctioning_club.FH]: {
    [Constants.show_type.rally_obedience]: {
      [Constants.dog_class_element_level.fh_rally_foundation_3_7]: [],
      [Constants.dog_class_element_level.fh_rally_foundation_8_up]: [],
      [Constants.dog_class_element_level.fh_rally_novice_3_7]: [],
      [Constants.dog_class_element_level.fh_rally_novice_8_up]: [],
      [Constants.dog_class_element_level.fh_rally_intermediate_3_7]: [],
      [Constants.dog_class_element_level.fh_rally_intermediate_8_up]: [],
      [Constants.dog_class_element_level.fh_rally_advanced_3_7]: [],
      [Constants.dog_class_element_level.fh_rally_advanced_8_up]: [],
      [Constants.dog_class_element_level.fh_rally_excellent]: [],
      [Constants.dog_class_element_level.fh_rally_masters]: [],
    },
    [Constants.show_type.agility]: {
      [Constants.dog_class_element.fh_agility_jtt]: {
        levels: [
          Constants.dog_class_element_level.fh_agility_on_leash,
          Constants.dog_class_element_level.fh_agility_off_leash,
        ],
        sections: [
          Constants.dog_class_section.fh_agility_division_a,
          Constants.dog_class_section.fh_agility_division_b,
        ],
      },
      [Constants.dog_class_element.fh_agility_standard_no_weaves]: {
        levels: [
          Constants.dog_class_element_level.fh_agility_on_leash,
          Constants.dog_class_element_level.fh_agility_off_leash,
        ],
        sections: [
          Constants.dog_class_section.fh_agility_division_a,
          Constants.dog_class_section.fh_agility_division_b,
        ],
      },
      [Constants.dog_class_element.fh_agility_standard_with_weaves]: {
        levels: [
          Constants.dog_class_element_level.fh_agility_on_leash,
          Constants.dog_class_element_level.fh_agility_off_leash,
        ],
        sections: [
          Constants.dog_class_section.fh_agility_division_a,
          Constants.dog_class_section.fh_agility_division_b,
        ],
      },
      [Constants.dog_class_element.fh_agility_advanced_standard_with_weaves]: {
        levels: [
          Constants.dog_class_element_level.fh_agility_off_leash,
        ],
        sections: [
          Constants.dog_class_section.fh_agility_division_b,
        ],
      },
      [Constants.dog_class_element.fh_agility_advanced_jumpers_no_weaves]: {
        levels: [
          Constants.dog_class_element_level.fh_agility_off_leash,
        ],
        sections: [
          Constants.dog_class_section.fh_agility_division_b,
        ],
      },
    },
  },

  // AHBA - American Herding Breed Association
  // Competitive classes use simple level arrays (Level I/II/III).
  // Stock type is stored as a section via showSectionAssociation (show_elements have level + section).
  // Non-competitive test classes (JHD, HCT) have no formal levels — stock type is stored directly
  // as the level value (same pattern as AKC Pre-Trial Herding).
  [Constants.sanctioning_club.AHBA]: {
    [Constants.show_type.herding]: {
      [Constants.dog_class_element.ahba_htd]: [
        Constants.dog_class_element_level.ahba_level_i,
        Constants.dog_class_element_level.ahba_level_ii,
        Constants.dog_class_element_level.ahba_level_iii,
      ],
      [Constants.dog_class_element.ahba_htad]: [
        Constants.dog_class_element_level.ahba_level_i,
        Constants.dog_class_element_level.ahba_level_ii,
        Constants.dog_class_element_level.ahba_level_iii,
      ],
      [Constants.dog_class_element.ahba_hrd]: [
        Constants.dog_class_element_level.ahba_level_i,
        Constants.dog_class_element_level.ahba_level_ii,
        Constants.dog_class_element_level.ahba_level_iii,
      ],
      [Constants.dog_class_element.ahba_rlf]: [
        Constants.dog_class_element_level.ahba_level_i,
        Constants.dog_class_element_level.ahba_level_ii,
        Constants.dog_class_element_level.ahba_level_iii,
      ],
      [Constants.dog_class_element.ahba_btd]: [
        Constants.dog_class_element_level.ahba_level_i,
        Constants.dog_class_element_level.ahba_level_ii,
        Constants.dog_class_element_level.ahba_level_iii,
      ],
      // Test classes have no formal levels — stock type is selected directly as section
      [Constants.dog_class_element.ahba_jhd]: [],
      [Constants.dog_class_element.ahba_hct]: [],
    },
  },
};

/**
 * Helper function to normalize element access
 * Handles both simple arrays and complex config objects
 */
export function getElementConfig(
  sanctioningClub: string,
  showType: string,
  elementName: string
): IShowTypeElementConfig | undefined {
  const element = ShowTypes[sanctioningClub]?.[showType]?.[elementName];
  
  if (!element) {
    return undefined;
  }
  
  // If it's already a config object, return it
  if (typeof element === 'object' && 'levels' in element) {
    return element as IShowTypeElementConfig;
  }
  
  // If it's an array, convert to config object format
  if (Array.isArray(element)) {
    return {
      levels: element,
      sections: undefined,
    };
  }
  
  return undefined;
}

/**
 * Sections offered for one level of an element. Defaults to the element's section list;
 * UKC Rally narrows it per level (RO1: A/B/C, RO2-ROM: A/B, URX/RMX: none).
 */
export function getLevelSections(
  sanctioningClub: string,
  showType: string,
  elementName: string,
  level: string,
  elementSections?: string[]
): string[] {
  if (isUkcRallyShow(sanctioningClub, showType)) {
    return ukcRallyLevelSections(level);
  }
  return elementSections ?? getElementConfig(sanctioningClub, showType, elementName)?.sections ?? [];
}

/**
 * Get all elements for a specific show type
 */
export function getShowTypeElements(
  sanctioningClub: string,
  showType: string
): { [elementName: string]: string[] | IShowTypeElementConfig } | undefined {
  return ShowTypes[sanctioningClub]?.[showType];
}

/**
 * Check if a show type uses sections for class organization
 * 
 * Sections are used by:
 * - UKC Nosework (sections A and B)
 * - C-WAGS Obedience
 * - C-WAGS Rally
 * - AKC Herding
 * - Country Rat Race
 */
export function showTypeUsesSections(
  sanctioningClub: string,
  showType: string
): boolean {
  // UKC Nosework uses sections A and B
  if (sanctioningClub === Constants.sanctioning_club.UKC && showType === Constants.show_type.nosework) {
    return true;
  }

  // UKC Rally uses sections A, B and C under each level
  if (isUkcRallyShow(sanctioningClub, showType)) {
    return true;
  }
  
  // C-WAGS Obedience uses sections
  if (sanctioningClub === Constants.sanctioning_club.C_WAGS && showType === Constants.show_type.obedience) {
    return true;
  }
  
  // C-WAGS Rally uses sections
  if (sanctioningClub === Constants.sanctioning_club.C_WAGS && showType === Constants.show_type.rally_obedience) {
    return true;
  }
  
  // AKC Herding can use sections
  if (sanctioningClub === Constants.sanctioning_club.AKC && showType === Constants.show_type.herding) {
    return true;
  }

  // AHBA Herding uses stock sections
  if (sanctioningClub === Constants.sanctioning_club.AHBA && showType === Constants.show_type.herding) {
    return true;
  }
  
  // Country Rat Race uses sections
  if (showType === Constants.show_type.country_rat_race) {
    return true;
  }

  // ABI Scent Dog Games uses Level 1 and Level 2 sections
  if (sanctioningClub === Constants.sanctioning_club.ABI && showType === Constants.show_type.scent_dog_games) {
    return true;
  }

  // 4-H Agility uses On-Leash / Off-Leash sections
  if (sanctioningClub === Constants.sanctioning_club.FH && showType === Constants.show_type.agility) {
    return true;
  }

  return false;
}

/** AKC Scent Work: whether Detective is one of the classes offered in the show setup. */
export function showOffersDetective(showElements?: { show_element?: string; level?: string }[]): boolean {
  return (showElements || []).some((se) => se.show_element === Constants.dog_class_element.scentwork_detective);
}
